import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database, SubscriptionTier } from "./types";

type Client = SupabaseClient<Database>;

const CSU_INSTITUTION_DOMAINS = new Set(["csuniv.edu", "student.csuniv.edu"]);

function isCsuInstitutionEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  const at = normalized.lastIndexOf("@");
  if (at <= 0 || at === normalized.length - 1) return false;
  return CSU_INSTITUTION_DOMAINS.has(normalized.slice(at + 1));
}

/**
 * Effective access tier for a user.
 *
 * Authenticated users with a confirmed @csuniv.edu or
 * @student.csuniv.edu address receive Institution access automatically.
 * Institution has the same content entitlements as Pro and no trainer
 * attempt cap. Everyone else continues through the existing lifetime,
 * Stripe subscription, and profile-tier logic unchanged.
 *
 * Pass an RLS-aware client when checking the current user's own tier.
 * Service-role calls that are not associated with a signed-in user simply
 * skip the CSU-domain override and use the persisted entitlement state.
 */
export async function getEffectiveTier(
  supabase: Client,
  userId: string,
): Promise<SubscriptionTier> {
  // Domain-based institutional entitlement. Email confirmation is required
  // by the project's Supabase auth configuration, and we also explicitly
  // require a confirmed timestamp here as defense in depth.
  try {
    if (typeof supabase.auth?.getUser === "function") {
      const { data } = await supabase.auth.getUser();
      const currentUser = data.user;
      if (
        currentUser?.id === userId &&
        currentUser.email_confirmed_at &&
        isCsuInstitutionEmail(currentUser.email)
      ) {
        return "institution";
      }
    }
  } catch {
    // Some trusted/service-role call sites do not carry a user session.
    // Fall through to the persisted entitlement checks below.
  }

  const [{ data: lifetime }, { data: subscription }, { data: profile }] = await Promise.all([
    supabase
      .from("lifetime_grants")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("subscriptions")
      .select("status, tier, current_period_end")
      .eq("user_id", userId)
      .order("current_period_end", { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase.from("profiles").select("tier").eq("id", userId).maybeSingle(),
  ]);

  if (lifetime) return "lifetime";

  if (subscription) {
    const isActive =
      subscription.status === "active" ||
      subscription.status === "trialing" ||
      subscription.status === "past_due";
    const periodOk =
      !subscription.current_period_end ||
      new Date(subscription.current_period_end).getTime() > Date.now();
    if (isActive && periodOk) return subscription.tier;
  }

  return profile?.tier ?? "free";
}

/**
 * Numerical rank used to compare tiers — higher = more access.
 * Institution sits at the same rank as Pro for content gating; it's a
 * billing distinction, not an entitlement difference.
 */
const TIER_RANK: Record<SubscriptionTier, number> = {
  free: 0,
  trainee: 1,
  pro: 2,
  institution: 2,
  lifetime: 3,
};

export function tierAtLeast(have: SubscriptionTier, need: SubscriptionTier): boolean {
  return (TIER_RANK[have] ?? 0) >= (TIER_RANK[need] ?? 0);
}

/**
 * Minimum tier required to access the sim. Currently set to `pro`:
 * the simulator is the headline Pro/Lifetime feature; Trainee is the
 * spaced-repetition-only entry tier.
 *
 * Override via the `SIM_REQUIRED_TIER` env var when running the sim
 * middleware. Accepts `trainee`, `pro`, `institution`, or `lifetime`.
 */
export const DEFAULT_SIM_REQUIRED_TIER: SubscriptionTier = "pro";

export function hasSimAccess(
  effective: SubscriptionTier,
  required: SubscriptionTier = DEFAULT_SIM_REQUIRED_TIER,
): boolean {
  return tierAtLeast(effective, required);
}

/**
 * True when the given role string is the instructor role.
 *
 * Accepts the raw `profiles.role` column (null-safe) because most
 * call sites have only the role string in hand, not a full Profile.
 * Admins are NOT treated as instructors here — `requireAdmin` and
 * `requireInstructor` are independent gates. Grant both roles via
 * the admin tool if a user should appear on both surfaces.
 */
export function isInstructor(role: string | null | undefined): boolean {
  return role === "instructor";
}
